import { Drawer, FocusTrap, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ContentLoader from 'react-content-loader';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import { MobileFilterCheckbox } from './MobileFilterCheckbox';
import { ReactComponent as IconClose } from 'assets/img/close.svg';
import { ReactComponent as IconSearch } from './img/iconSearch.svg';
import styles from './MobileFilterDrawer.module.scss';

export interface FilterOption {
  id: string;
  label: string;
}

interface MobileFilterDrawerProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onApply: () => void;
  onReset: () => void;
  showSearch?: boolean;
  serverList?: boolean;
  onServerListSearch?: (search: string) => void;
  serverListHasNext?: boolean;
  onServerListLoadMore?: () => void;
  serverListFetching?: boolean;
  serverListLoading?: boolean;
}

export const MobileFilterDrawer: React.FC<MobileFilterDrawerProps> = ({
  opened,
  onClose,
  title,
  options,
  selectedValues,
  onChange,
  onApply,
  onReset,
  showSearch = false,
  serverList = false,
  onServerListSearch,
  serverListHasNext = false,
  onServerListLoadMore,
  serverListFetching = false,
  serverListLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery.trim(), 300);
  const loadMoreLockRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!serverList || !opened) return;
    onServerListSearch?.(debouncedSearch);
  }, [debouncedSearch, serverList, opened, onServerListSearch]);

  useEffect(() => {
    if (!serverListFetching) loadMoreLockRef.current = false;
  }, [serverListFetching]);

  const handleLoadMore = useCallback(() => {
    if (
      !serverList ||
      !onServerListLoadMore ||
      !serverListHasNext ||
      serverListFetching ||
      loadMoreLockRef.current
    ) {
      return;
    }
    loadMoreLockRef.current = true;
    onServerListLoadMore();
  }, [serverList, onServerListLoadMore, serverListHasNext, serverListFetching]);

  const handleScroll = useCallback(() => {
    if (!serverList) return;
    const el = scrollRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gap < 72 && el.scrollTop > 12) handleLoadMore();
  }, [serverList, handleLoadMore]);

  const filteredOptions = useMemo(() => {
    const valid = options.filter((o) => o.label != null && String(o.label).trim() !== '');
    if (serverList) return valid;
    if (!showSearch || !searchQuery.trim()) return valid;
    return valid.filter((o) => o.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [options, searchQuery, showSearch, serverList]);

  const handleOptionChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, optionId]);
    } else {
      onChange(selectedValues.filter((id) => id !== optionId));
    }
  };

  const handleReset = () => {
    onChange([]);
    onReset();
  };

  const handleApply = () => {
    onApply();
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  useEffect(() => {
    if (!opened) setSearchQuery('');
  }, [opened]);

  const showSearchRow = showSearch || serverList;
  const listEmpty = filteredOptions.length === 0;
  const showListSkeleton = serverList && serverListLoading && listEmpty;

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position='bottom'
      zIndex={1000}
      withCloseButton={false}
      styles={{
        content: {
          height: 'fit-content',
          maxHeight: '90vh',
          borderRadius: '13px 13px 0px 0px',
          backgroundColor: '#FFF',
          padding: '16px 16px 4px 16px',
          boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflow: 'hidden',
        },
      }}>
      <FocusTrap.InitialFocus />
      <div className={styles.header}>
        <span className={styles.headerTitle}>{title}</span>
        <span className={styles.headerClear} onClick={handleClose}>
          Готово
        </span>
      </div>

      {showSearchRow && (
        <div className={styles.searchWrapper}>
          <IconSearch className={styles.searchIcon} />
          <input
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Искать...'
            className={styles.searchInput}
          />
          {searchQuery && (
            <IconClose className={styles.closeIcon} onClick={handleClearSearch} />
          )}
        </div>
      )}

      <div
        ref={serverList ? scrollRef : undefined}
        className={styles.selectItems}
        onScroll={serverList ? handleScroll : undefined}>
        {showListSkeleton ? (
          <ContentLoader
            speed={2}
            height={150}
            width='100%'
            backgroundColor='#FFFFFF'
            foregroundColor='#D8E4FB8C'>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        ) : listEmpty ? (
          <div className={styles.noResults}>
            <span>Ничего не найдено</span>
          </div>
        ) : (
          <>
            {filteredOptions.map((option) => {
              const isChecked = selectedValues.includes(option.id);
              return (
                <div
                  key={option.id}
                  className={styles.selectItem}
                  onClick={() => handleOptionChange(option.id, !isChecked)}>
                  <span className={styles.selectItemLabel}>{option.label}</span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <MobileFilterCheckbox
                      checked={isChecked}
                      onChange={(checked) => handleOptionChange(option.id, checked)}
                    />
                  </div>
                </div>
              );
            })}
            {serverList && serverListFetching && serverListHasNext && (
              <div className={styles.loadMoreRow}>
                <Loader size='xs' color='#002CFB' />
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles.actionsRow}>
        <FilterButton variant='reset' onClick={handleReset} disabled={false}>
          Сбросить
        </FilterButton>
        <FilterButton variant='apply' onClick={handleApply} disabled={false}>
          Сохранить
        </FilterButton>
      </div>
    </Drawer>
  );
};
